/**
 * useIdleTimeout Hook
 *
 * Bug #021: Implements automatic logout after period of inactivity.
 * Tracks user interactions and logs out after configured idle time.
 */

import { useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';

// Default timeout: 15 minutes (in milliseconds)
const DEFAULT_TIMEOUT = 15 * 60 * 1000;

// Events that indicate user activity
const ACTIVITY_EVENTS = [
  'mousedown',
  'mousemove',
  'keypress',
  'scroll',
  'touchstart',
  'click',
];

/**
 * Hook to automatically logout user after idle timeout
 *
 * @param {number} timeout - Timeout in milliseconds (default: 15 minutes)
 */
const useIdleTimeout = (timeout = DEFAULT_TIMEOUT) => {
  const { isAuthenticated, logout } = useAuth();
  const timeoutId = useRef(null);

  /**
   * Reset the idle timer
   */
  const resetTimer = useCallback(() => {
    // Clear existing timeout
    if (timeoutId.current) {
      clearTimeout(timeoutId.current);
    }

    // Only set timeout if user is authenticated
    if (isAuthenticated()) {
      timeoutId.current = setTimeout(() => {
        console.log('User inactive - logging out');
        logout('Session expired due to inactivity');
      }, timeout);
    }
  }, [isAuthenticated, logout, timeout]);

  /**
   * Handle user activity
   */
  const handleActivity = useCallback(() => {
    resetTimer();
  }, [resetTimer]);

  /**
   * Setup and cleanup event listeners
   */
  useEffect(() => {
    // Don't setup idle timeout if user is not authenticated
    if (!isAuthenticated()) {
      return;
    }

    // Initial timer setup
    resetTimer();

    // Add event listeners for user activity
    ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Cleanup function
    return () => {
      // Remove event listeners
      ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });

      // Clear timeout
      if (timeoutId.current) {
        clearTimeout(timeoutId.current);
      }
    };
  }, [isAuthenticated, resetTimer, handleActivity]);

  return { resetTimer };
};

export default useIdleTimeout;
